// ============================================
// WhatsApp Cockpit - 3-Panel Desktop, Tabs Mobile
// ============================================

import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Send,
  Sparkles,
  RefreshCw,
  User,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Loader2,
  FileText,
  Clock
} from 'lucide-react';
import { useUIStore, useToast } from '../stores';
import { messagesApi, type Conversation, type Message } from '../lib/api';
import { EmptyState, Spinner, PageLoader, StageBadge } from '../components';
import { cn, formatRelativeTime, formatTime, truncate } from '../lib/utils';
import { useTranslation } from '../i18n/useTranslation';

type Tab = 'conversations' | 'chat' | 'ai';

export function WhatsAppCockpit() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  const activeProject = useUIStore((state) => state.activeProject);

  const [activeTab, setActiveTab] = useState<Tab>('conversations');
  const [selectedConversation, setSelectedConversation] = useState<string | null>(
    searchParams.get('conversation')
  );
  const [messageText, setMessageText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: conversationsData, isLoading: conversationsLoading } = useQuery({
    queryKey: ['conversations', activeProject],
    queryFn: () => messagesApi.listConversations(
      activeProject === 'all' ? undefined : { project_id: activeProject }
    ),
    refetchInterval: 10000,
  });

  const { data: messagesData, isLoading: messagesLoading } = useQuery({
    queryKey: ['messages', selectedConversation],
    queryFn: () => messagesApi.getMessages(selectedConversation!),
    enabled: !!selectedConversation,
    refetchInterval: 5000,
  });

  const { data: aiData, isLoading: aiLoading, refetch: refetchAI } = useQuery({
    queryKey: ['ai', selectedConversation],
    queryFn: () => messagesApi.getAI(selectedConversation!),
    enabled: !!selectedConversation,
    staleTime: 60000,
  });

  const sendMutation = useMutation({
    mutationFn: ({ conversationId, text }: { conversationId: string; text: string }) =>
      messagesApi.send(conversationId, text),
    onSuccess: () => {
      setMessageText('');
      queryClient.invalidateQueries({ queryKey: ['messages', selectedConversation] });
      toast.success(t('whatsapp.sent'));
    },
    onError: (error) => {
      toast.error(t('whatsapp.sendError'), String(error));
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messagesData?.messages]);

  const handleSelectConversation = (id: string) => {
    setSelectedConversation(id);
    setActiveTab('chat');
  };

  const handleSend = () => {
    if (!messageText.trim() || !selectedConversation) return;
    sendMutation.mutate({ conversationId: selectedConversation, text: messageText.trim() });
  };

  const handleUseSuggestion = (text: string) => {
    setMessageText(text);
    setActiveTab('chat');
  };

  const conversations = conversationsData?.conversations || [];
  const messages = messagesData?.messages || [];
  const selectedConvo = conversations.find(c => c.id === selectedConversation);

  return (
    <div className="h-[calc(100vh-8rem)]">
      {/* Mobile Tabs */}
      <div className="lg:hidden flex border-b border-slate-800 mb-4">
        <TabButton active={activeTab === 'conversations'} onClick={() => setActiveTab('conversations')}>
          {t('whatsapp.conversations')}
        </TabButton>
        <TabButton active={activeTab === 'chat'} onClick={() => setActiveTab('chat')} disabled={!selectedConversation}>
          {t('whatsapp.chat')}
        </TabButton>
        <TabButton active={activeTab === 'ai'} onClick={() => setActiveTab('ai')} disabled={!selectedConversation}>
          AI
        </TabButton>
      </div>

      <div className="h-full flex gap-4">
        {/* Panel 1: Conversations */}
        <div className={cn('w-full lg:w-80 flex-shrink-0 card overflow-hidden flex flex-col', activeTab !== 'conversations' && 'hidden lg:flex')}>
          <div className="p-4 border-b border-slate-800">
            <h2 className="font-semibold text-white">{t('whatsapp.conversations')}</h2>
            <p className="text-xs text-slate-500 mt-1">{conversations.length} {t('whatsapp.conversationCount')}</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversationsLoading ? <div className="flex items-center justify-center py-8"><Spinner /></div>
            : conversations.length === 0 ? <EmptyState title={t('whatsapp.noConversations')} description={t('whatsapp.noConversationsDesc')} />
            : conversations.map((convo) => <ConversationItem key={convo.id} conversation={convo} selected={convo.id === selectedConversation} onClick={() => handleSelectConversation(convo.id)} />)}
          </div>
        </div>

        {/* Panel 2: Chat */}
        <div className={cn('flex-1 card overflow-hidden flex flex-col', activeTab !== 'chat' && 'hidden lg:flex')}>
          {selectedConversation && selectedConvo ? (
            <>
              <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button onClick={() => setActiveTab('conversations')} className="lg:hidden btn-icon"><ChevronRight className="w-5 h-5" /></button>
                  <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center"><User className="w-5 h-5 text-slate-400" /></div>
                  <div>
                    <p className="font-medium text-white">{selectedConvo.contact_name}</p>
                    <p className="text-xs text-slate-500">{selectedConvo.contact_phone_masked}</p>
                  </div>
                </div>
                <button onClick={() => setActiveTab('ai')} className="lg:hidden btn-icon text-primary-400"><Sparkles className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messagesLoading ? <div className="flex items-center justify-center py-8"><Spinner /></div>
                : messages.length === 0 ? <EmptyState title={t('whatsapp.noMessages')} description={t('whatsapp.startChat')} />
                : messages.map((msg) => <MessageBubble key={msg.id} message={msg} />)}
                <div ref={messagesEndRef} />
              </div>
              <div className="p-4 border-t border-slate-800">
                <div className="flex gap-2">
                  <input type="text" value={messageText} onChange={(e) => setMessageText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()} placeholder={t('whatsapp.typeMessage')} className="input flex-1" />
                  <button onClick={handleSend} className="btn-primary" disabled={!messageText.trim() || sendMutation.isPending}>
                    {sendMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </>
          ) : <EmptyState title={t('whatsapp.selectConversation')} description={t('whatsapp.selectConversationDesc')} />}
        </div>

        {/* Panel 3: AI */}
        <div className={cn('w-full lg:w-80 flex-shrink-0 card overflow-hidden flex flex-col', activeTab !== 'ai' && 'hidden lg:flex')}>
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary-400" /><h2 className="font-semibold text-white">{t('whatsapp.aiAssistant')}</h2></div>
            {selectedConversation && <button onClick={() => refetchAI()} className="btn-icon text-slate-400 hover:text-white" disabled={aiLoading}><RefreshCw className={cn('w-4 h-4', aiLoading && 'animate-spin')} /></button>}
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {!selectedConversation ? <EmptyState title={t('whatsapp.selectConversation')} description={t('whatsapp.selectForAI')} />
            : aiLoading ? <div className="flex items-center justify-center py-8"><Spinner /></div>
            : (
              <>
                {aiData?.summary && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-slate-400"><FileText className="w-4 h-4" /><span className="text-sm font-medium">{t('whatsapp.conversationSummary')}</span></div>
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <p className="text-sm text-slate-300">{aiData.summary.summary}</p>
                      {aiData.summary.key_points?.length > 0 && <ul className="mt-2 space-y-1">{aiData.summary.key_points.map((point, i) => <li key={i} className="text-xs text-slate-400 flex items-start gap-2"><span className="text-primary-400"></span>{point}</li>)}</ul>}
                    </div>
                  </div>
                )}
                {aiData?.suggestions?.suggestions && aiData.suggestions.suggestions.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-slate-400"><Lightbulb className="w-4 h-4" /><span className="text-sm font-medium">{t('whatsapp.suggestedReplies')}</span></div>
                    <div className="space-y-2">{aiData.suggestions.suggestions.map((suggestion, i) => <button key={i} onClick={() => handleUseSuggestion(suggestion)} className="w-full text-right bg-slate-800/50 hover:bg-slate-800 rounded-lg p-3 transition-colors"><p className="text-sm text-slate-300">{suggestion}</p></button>)}</div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="lg:hidden p-4 border-t border-slate-800">
            <button onClick={() => setActiveTab('chat')} className="btn-secondary w-full"><ChevronLeft className="w-4 h-4" />{t('whatsapp.backToChat')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface TabButtonProps { children: React.ReactNode; active: boolean; onClick: () => void; disabled?: boolean; }

function TabButton({ children, active, onClick, disabled }: TabButtonProps) {
  return <button onClick={onClick} disabled={disabled} className={cn('flex-1 py-3 text-sm font-medium border-b-2 transition-colors', active ? 'border-primary-500 text-primary-400' : 'border-transparent text-slate-400 hover:text-white', disabled && 'opacity-50 cursor-not-allowed')}>{children}</button>;
}

interface ConversationItemProps { conversation: Conversation; selected: boolean; onClick: () => void; }

function ConversationItem({ conversation, selected, onClick }: ConversationItemProps) {
  return (
    <button onClick={onClick} className={cn('w-full p-4 text-right border-b border-slate-800/50 transition-colors', selected ? 'bg-primary-900/20' : 'hover:bg-slate-800/50')}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0"><User className="w-5 h-5 text-slate-400" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between"><p className="font-medium text-white truncate">{conversation.contact_name}</p><span className="text-2xs text-slate-500">{formatRelativeTime(conversation.last_message_at)}</span></div>
          <p className="text-sm text-slate-400 truncate mt-0.5">{conversation.summary || conversation.contact_phone_masked}</p>
        </div>
        {conversation.unread_count > 0 && <span className="bg-primary-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">{conversation.unread_count}</span>}
      </div>
    </button>
  );
}

interface MessageBubbleProps { message: Message; }

function MessageBubble({ message }: MessageBubbleProps) {
  const isOutgoing = message.direction === 'out';
  return (
    <div className={cn('flex', isOutgoing ? 'justify-start' : 'justify-end')}>
      <div className={isOutgoing ? 'wa-bubble-out' : 'wa-bubble-in'}>
        <p className="text-sm text-white whitespace-pre-wrap">{message.body}</p>
        <div className="flex items-center justify-end gap-1 mt-1">
          <span className="text-2xs text-slate-500">{formatTime(message.sent_at)}</span>
          {isOutgoing && <span className={cn('text-2xs', message.status === 'delivered' || message.status === 'read' ? 'text-blue-400' : 'text-slate-500')}>{message.status === 'read' ? '' : message.status === 'delivered' ? '' : ''}</span>}
        </div>
      </div>
    </div>
  );
}
